import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { LabClient } from '@alpha-edge/shared'
import { api } from '../../services/api'
import { useApiList } from '../../hooks/use-api-list'
import { colors } from '../../components/ui/styles'
import { EHR_TEMPLATES, type EhrTemplate } from './templates'

// ---- shared styles -------------------------------------------------------

const cardStyle = {
  padding: '1.5rem',
  background: colors.bgCard,
  borderRadius: '8px',
  border: `1px solid ${colors.border}`,
}

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: `1px solid #d1d5db`,
  borderRadius: '6px',
  fontSize: '0.875rem',
  boxSizing: 'border-box' as const,
}

const labelStyle = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500 as const,
  marginBottom: '0.25rem',
  color: '#374151',
}

const btnStyle = {
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 500 as const,
}

// ---- types ---------------------------------------------------------------

type TransportType = 'sftp' | 'soap'

interface SftpFields {
  host: string
  port: string
  authType: 'password' | 'pem'
  username: string
  credential: string
  inboxPath: string
  outboxPath: string
}

interface SoapFields {
  endpoint: string
  wsdlUrl: string
  authType: 'credentials' | 'apikey' | 'oauth'
  username: string
  credential: string
}

function getTransportType(template: EhrTemplate): TransportType {
  const t = template.configJson.transport as Record<string, unknown> | undefined
  return t?.type === 'soap' ? 'soap' : 'sftp'
}

function sftpFromTemplate(template: EhrTemplate): SftpFields {
  const t = (template.configJson.transport ?? {}) as Record<string, unknown>
  return {
    host: typeof t.host === 'string' ? t.host : '',
    port: typeof t.port === 'number' ? String(t.port) : '22',
    authType: t.authType === 'pem' ? 'pem' : 'password',
    username: typeof t.username === 'string' ? t.username : '',
    credential: typeof t.credential === 'string' ? t.credential : '',
    inboxPath: typeof t.inboxPath === 'string' ? t.inboxPath : '/inbox',
    outboxPath: typeof t.outboxPath === 'string' ? t.outboxPath : '/outbox',
  }
}

function soapFromTemplate(template: EhrTemplate): SoapFields {
  const t = (template.configJson.transport ?? {}) as Record<string, unknown>
  const auth = (t.auth ?? {}) as Record<string, unknown>
  return {
    endpoint: typeof t.endpoint === 'string' ? t.endpoint : '',
    wsdlUrl: typeof t.wsdlUrl === 'string' ? t.wsdlUrl : '',
    authType: auth.type === 'apikey' ? 'apikey' : auth.type === 'oauth' ? 'oauth' : 'credentials',
    username: typeof auth.username === 'string' ? auth.username : '',
    credential: typeof auth.credential === 'string' ? auth.credential : '',
  }
}

// ---- component -----------------------------------------------------------

const TOTAL_STEPS = 4

export function EhrOnboardingWizard() {
  const navigate = useNavigate()

  // Step state
  const [step, setStep] = useState(1)

  // Step 1 — template selection
  const [selectedTemplate, setSelectedTemplate] = useState<EhrTemplate | null>(null)

  // Step 2 — identity
  const [ehrId, setEhrId] = useState('')
  const [ehrName, setEhrName] = useState('')
  const [version, setVersion] = useState('1.0.0')

  // Step 2 — transport (sftp)
  const [sftp, setSftp] = useState<SftpFields>({
    host: '',
    port: '22',
    authType: 'password',
    username: '',
    credential: '',
    inboxPath: '/inbox',
    outboxPath: '/outbox',
  })

  // Step 2 — transport (soap)
  const [soap, setSoap] = useState<SoapFields>({
    endpoint: '',
    wsdlUrl: '',
    authType: 'credentials',
    username: '',
    credential: '',
  })

  // Step 3 — optional single facility shortcut
  const [facilityLabClientId, setFacilityLabClientId] = useState('')
  const [facilitySendingFacility, setFacilitySendingFacility] = useState('')
  const [facilityReceivingFacility, setFacilityReceivingFacility] = useState('')

  // Step 4 — save state
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedConfigId, setSavedConfigId] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  // Lab clients for the facility dropdown (starts fetching on mount)
  const { data: labClients } = useApiList<LabClient>({ path: '/lab-clients', limit: 200 })

  const transportType: TransportType = selectedTemplate ? getTransportType(selectedTemplate) : 'sftp'

  // When a template is picked, prefill identity + transport from it
  const pickTemplate = (t: EhrTemplate) => {
    setSelectedTemplate(t)
    setEhrId(t.id)
    setEhrName(t.name)
    setVersion('1.0.0')
    if (getTransportType(t) === 'sftp') {
      setSftp(sftpFromTemplate(t))
    } else {
      setSoap(soapFromTemplate(t))
    }
  }

  const assembleConfigJson = (): Record<string, unknown> => {
    if (!selectedTemplate) return {}

    const transport: Record<string, unknown> =
      transportType === 'sftp'
        ? {
            type: 'sftp',
            host: sftp.host,
            port: parseInt(sftp.port) || 22,
            authType: sftp.authType,
            username: sftp.username,
            credential: sftp.credential,
            inboxPath: sftp.inboxPath,
            outboxPath: sftp.outboxPath,
          }
        : {
            type: 'soap',
            endpoint: soap.endpoint,
            wsdlUrl: soap.wsdlUrl,
            auth: { type: soap.authType, username: soap.username, credential: soap.credential },
          }

    const existingFacilities = Array.isArray(selectedTemplate.configJson.facilities)
      ? (selectedTemplate.configJson.facilities as unknown[])
      : []

    const facilities = [
      ...existingFacilities,
      ...(facilityLabClientId || facilitySendingFacility || facilityReceivingFacility
        ? [
            {
              labClientId: facilityLabClientId || undefined,
              sendingFacility: facilitySendingFacility || undefined,
              receivingFacility: facilityReceivingFacility || undefined,
            },
          ]
        : []),
    ]

    return { ...selectedTemplate.configJson, transport, facilities }
  }

  const handleSave = async () => {
    if (!selectedTemplate) return
    setSaving(true)
    setSaveError(null)
    try {
      const result = await api.post<{ id: string }>('/ehr-configs', {
        ehrId,
        ehrName,
        version,
        enabled: false,
        configJson: assembleConfigJson(),
      })
      const newId = result.data?.id
      if (newId) {
        setSavedConfigId(newId)
      } else {
        navigate('/settings/ehr')
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save EHR config')
    } finally {
      setSaving(false)
    }
  }

  const handleTestConnection = async () => {
    if (!savedConfigId) return
    setTesting(true)
    setTestResult(null)
    try {
      const result = await api.post<{ success: boolean; message: string }>(
        `/ehr-configs/${savedConfigId}/test-connection`,
        {},
      )
      setTestResult(result.data ?? { success: false, message: 'No response' })
    } catch (err) {
      setTestResult({ success: false, message: err instanceof Error ? err.message : 'Test failed' })
    } finally {
      setTesting(false)
    }
  }

  // ---- step renderers ----------------------------------------------------

  const renderStep1 = () => (
    <div>
      <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>Choose a template</h2>
      <p style={{ margin: '0 0 1.5rem', color: colors.textMuted, fontSize: '0.875rem' }}>
        Pick the template closest to your EHR. You can adjust every field afterward on the edit page.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
        {EHR_TEMPLATES.map((t) => {
          const active = selectedTemplate?.id === t.id
          return (
            <button
              key={t.id}
              onClick={() => pickTemplate(t)}
              style={{
                padding: '1rem',
                borderRadius: '8px',
                border: `2px solid ${active ? colors.primary : colors.border}`,
                background: active ? '#eff6ff' : colors.bgCard,
                cursor: 'pointer',
                textAlign: 'left' as const,
              }}
              data-testid={`template-card-${t.id}`}
            >
              <div style={{ fontWeight: 600, marginBottom: '0.375rem', color: '#1e3a5f' }}>{t.name}</div>
              <div style={{ fontSize: '0.8125rem', color: colors.textMuted }}>{t.description}</div>
            </button>
          )
        })}
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '1rem' }}>
      <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>Identity &amp; Transport</h2>

      {/* Credential warning banner */}
      <div
        style={{
          padding: '0.75rem',
          background: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '6px',
          color: '#92400e',
          fontSize: '0.8125rem',
        }}
      >
        Credentials typed here are saved into the config JSON in plaintext for V1. Migrate them to the tenant credential
        vault after onboarding.
      </div>

      <div>
        <label style={labelStyle}>EHR ID (slug, unique)</label>
        <input
          style={inputStyle}
          value={ehrId}
          onChange={(e) => setEhrId(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
          placeholder="e.g., pcc-opentext"
          data-testid="ehr-id-input"
        />
      </div>
      <div>
        <label style={labelStyle}>EHR Name</label>
        <input
          style={inputStyle}
          value={ehrName}
          onChange={(e) => setEhrName(e.target.value)}
          placeholder="e.g., PCC / OpenText"
          data-testid="ehr-name-input"
        />
      </div>
      <div>
        <label style={labelStyle}>Version</label>
        <input
          style={inputStyle}
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          data-testid="version-input"
        />
      </div>

      <hr style={{ margin: '0.25rem 0', border: 'none', borderTop: `1px solid ${colors.border}` }} />

      {transportType === 'sftp' && (
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
          <div style={{ fontWeight: 600, color: '#374151', fontSize: '0.9375rem' }}>SFTP Transport</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Host</label>
              <input
                style={inputStyle}
                value={sftp.host}
                onChange={(e) => setSftp({ ...sftp, host: e.target.value })}
                placeholder="sftp.example.com"
                data-testid="sftp-host"
              />
            </div>
            <div>
              <label style={labelStyle}>Port</label>
              <input
                style={inputStyle}
                value={sftp.port}
                onChange={(e) => setSftp({ ...sftp, port: e.target.value })}
                placeholder="22"
                data-testid="sftp-port"
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Auth Type</label>
            <select
              style={{ ...inputStyle }}
              value={sftp.authType}
              onChange={(e) => setSftp({ ...sftp, authType: e.target.value as 'password' | 'pem' })}
              data-testid="sftp-auth-type"
            >
              <option value="password">Password</option>
              <option value="pem">PEM Key</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Username</label>
            <input
              style={inputStyle}
              value={sftp.username}
              onChange={(e) => setSftp({ ...sftp, username: e.target.value })}
              data-testid="sftp-username"
            />
          </div>
          <div>
            <label style={labelStyle}>{sftp.authType === 'pem' ? 'PEM Key (paste)' : 'Password'}</label>
            <input
              type="password"
              style={inputStyle}
              value={sftp.credential}
              onChange={(e) => setSftp({ ...sftp, credential: e.target.value })}
              data-testid="sftp-credential"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Inbox Path</label>
              <input
                style={inputStyle}
                value={sftp.inboxPath}
                onChange={(e) => setSftp({ ...sftp, inboxPath: e.target.value })}
                data-testid="sftp-inbox-path"
              />
            </div>
            <div>
              <label style={labelStyle}>Outbox Path</label>
              <input
                style={inputStyle}
                value={sftp.outboxPath}
                onChange={(e) => setSftp({ ...sftp, outboxPath: e.target.value })}
                data-testid="sftp-outbox-path"
              />
            </div>
          </div>
        </div>
      )}

      {transportType === 'soap' && (
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem' }}>
          <div style={{ fontWeight: 600, color: '#374151', fontSize: '0.9375rem' }}>SOAP Transport</div>
          <div>
            <label style={labelStyle}>Endpoint URL</label>
            <input
              style={inputStyle}
              value={soap.endpoint}
              onChange={(e) => setSoap({ ...soap, endpoint: e.target.value })}
              placeholder="https://ehr.example.com/hl7/service"
              data-testid="soap-endpoint"
            />
          </div>
          <div>
            <label style={labelStyle}>WSDL URL (optional)</label>
            <input
              style={inputStyle}
              value={soap.wsdlUrl}
              onChange={(e) => setSoap({ ...soap, wsdlUrl: e.target.value })}
              placeholder="https://ehr.example.com/hl7/service?wsdl"
              data-testid="soap-wsdl-url"
            />
          </div>
          <div>
            <label style={labelStyle}>Auth Type</label>
            <select
              style={{ ...inputStyle }}
              value={soap.authType}
              onChange={(e) => setSoap({ ...soap, authType: e.target.value as SoapFields['authType'] })}
              data-testid="soap-auth-type"
            >
              <option value="credentials">Credentials</option>
              <option value="apikey">API Key</option>
              <option value="oauth">OAuth</option>
            </select>
          </div>
          {soap.authType !== 'oauth' && (
            <>
              <div>
                <label style={labelStyle}>Username / Key ID</label>
                <input
                  style={inputStyle}
                  value={soap.username}
                  onChange={(e) => setSoap({ ...soap, username: e.target.value })}
                  data-testid="soap-username"
                />
              </div>
              <div>
                <label style={labelStyle}>{soap.authType === 'apikey' ? 'API Key' : 'Password'}</label>
                <input
                  type="password"
                  style={inputStyle}
                  value={soap.credential}
                  onChange={(e) => setSoap({ ...soap, credential: e.target.value })}
                  data-testid="soap-credential"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )

  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '1rem' }}>
      <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>Facilities (optional)</h2>
      <p style={{ margin: 0, color: colors.textMuted, fontSize: '0.875rem' }}>
        You can add per-lab-client facility overrides after the integration is saved, via the Facilities tab on the EHR
        edit page.
      </p>
      <p style={{ margin: 0, color: colors.textMuted, fontSize: '0.875rem' }}>
        Optionally add a single facility shortcut now:
      </p>

      <div>
        <label style={labelStyle}>Lab Client</label>
        <select
          style={{ ...inputStyle }}
          value={facilityLabClientId}
          onChange={(e) => setFacilityLabClientId(e.target.value)}
          data-testid="facility-lab-client-select"
        >
          <option value="">-- None --</option>
          {(labClients as readonly LabClient[]).map((lc) => (
            <option key={lc.id} value={lc.id}>
              {lc.code} - {lc.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label style={labelStyle}>Sending Facility</label>
        <input
          style={inputStyle}
          value={facilitySendingFacility}
          onChange={(e) => setFacilitySendingFacility(e.target.value)}
          placeholder="e.g., MYLAB"
          data-testid="facility-sending"
        />
      </div>
      <div>
        <label style={labelStyle}>Receiving Facility</label>
        <input
          style={inputStyle}
          value={facilityReceivingFacility}
          onChange={(e) => setFacilityReceivingFacility(e.target.value)}
          placeholder="e.g., PCCRECEIVER"
          data-testid="facility-receiving"
        />
      </div>
    </div>
  )

  const renderStep4 = () => {
    const configJson = assembleConfigJson()
    const transport = configJson.transport as Record<string, unknown> | undefined
    const facilities = Array.isArray(configJson.facilities) ? configJson.facilities : []

    return (
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '1rem' }}>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.125rem' }}>Review &amp; Save</h2>

        {saveError && (
          <div
            style={{
              padding: '0.75rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {saveError}
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.875rem' }}>
          <tbody>
            {[
              ['EHR ID', ehrId],
              ['EHR Name', ehrName],
              ['Version', version],
              ['Transport', String(transport?.type ?? '')],
              ['Facilities', String(facilities.length)],
              ['Status', 'Disabled (draft)'],
            ].map(([k, v]) => (
              <tr key={k} style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500, color: '#374151', width: '40%' }}>{k}</td>
                <td style={{ padding: '0.5rem 0.75rem', color: colors.textMuted }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <details style={{ fontSize: '0.8125rem', color: colors.textMuted }}>
          <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>Full config JSON</summary>
          <pre
            style={{
              margin: 0,
              padding: '0.75rem',
              background: colors.bgMuted,
              borderRadius: '6px',
              overflowX: 'auto',
              maxHeight: '320px',
              overflowY: 'auto',
              fontSize: '0.75rem',
            }}
          >
            {JSON.stringify(configJson, null, 2)}
          </pre>
        </details>

        {savedConfigId && (
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.5rem' }}>
            <div
              style={{
                padding: '0.75rem',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '6px',
                color: '#16a34a',
                fontSize: '0.875rem',
              }}
            >
              EHR config created successfully.
            </div>
            {testResult && (
              <div
                data-testid="test-connection-result"
                style={{
                  padding: '0.75rem',
                  background: testResult.success ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
                  borderRadius: '6px',
                  color: testResult.success ? '#16a34a' : '#dc2626',
                  fontSize: '0.875rem',
                }}
              >
                {testResult.success ? 'Connection successful' : `Connection failed: ${testResult.message}`}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleTestConnection}
                disabled={testing}
                style={{ ...btnStyle, background: '#f1f5f9', color: '#374151', opacity: testing ? 0.6 : 1 }}
                data-testid="test-connection-btn"
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                onClick={() => navigate(`/settings/ehr/${savedConfigId}`)}
                style={{ ...btnStyle, background: colors.primary, color: '#fff' }}
              >
                Go to Config
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  const stepContent = [renderStep1, renderStep2, renderStep3, renderStep4][step - 1]

  // ---- render ------------------------------------------------------------

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '1.5rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
          <button
            onClick={() => navigate('/settings/ehr')}
            style={{ ...btnStyle, background: 'transparent', color: colors.textMuted, padding: '0' }}
          >
            &larr; EHR Configurations
          </button>
        </div>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>New EHR Integration Wizard</h1>
        <div style={{ color: colors.textMuted, fontSize: '0.875rem', marginTop: '0.25rem' }}>
          Step {step} of {TOTAL_STEPS}
        </div>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem' }}>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => {
          const n = i + 1
          const done = n < step
          const active = n === step
          return (
            <div
              key={n}
              style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                background: done || active ? colors.primary : colors.border,
                opacity: done ? 0.5 : 1,
              }}
            />
          )
        })}
      </div>

      <div style={cardStyle}>{stepContent?.()}</div>

      {/* Navigation footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '1.25rem',
        }}
      >
        <button
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
          style={{
            ...btnStyle,
            background: '#f1f5f9',
            color: step === 1 ? colors.textMuted : '#374151',
            opacity: step === 1 ? 0.5 : 1,
          }}
          data-testid="back-btn"
        >
          Back
        </button>

        {step < TOTAL_STEPS ? (
          <button
            onClick={() => setStep((s) => Math.min(TOTAL_STEPS, s + 1))}
            disabled={step === 1 && selectedTemplate === null}
            style={{
              ...btnStyle,
              background: step === 1 && selectedTemplate === null ? '#94a3b8' : colors.primary,
              color: '#fff',
              cursor: step === 1 && selectedTemplate === null ? 'not-allowed' : 'pointer',
            }}
            data-testid="next-btn"
          >
            Next
          </button>
        ) : savedConfigId ? (
          <button
            onClick={() => navigate(`/settings/ehr/${savedConfigId}`)}
            style={{ ...btnStyle, background: colors.primary, color: '#fff' }}
            data-testid="go-to-config-btn"
          >
            Open Config
          </button>
        ) : (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...btnStyle,
              background: saving ? '#94a3b8' : colors.primary,
              color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
            data-testid="save-btn"
          >
            {saving ? 'Saving...' : 'Save (disabled)'}
          </button>
        )}
      </div>
    </div>
  )
}
